// /app/frontend/src/components/frugal-v2/components/EmailDetailModal.jsx
// Email approval modal with account selection (< 250 lines)

import React, { useState, useEffect } from 'react';
import { X, Mail, Calendar, CreditCard, Tag, Eye, EyeOff, Link2, Plus, AlertCircle } from 'lucide-react';
import { translateCategory, INCOME_CATEGORIES } from '../constants/categories';
import { getCategories } from './emails/emailConstants';
import AccountSelector from './AccountSelector';
import RawEmailView from './RawEmailView';
import InformativeEmailModal from './InformativeEmailModal';

const EmailDetailModal = ({ 
  email, onClose, onApprove, onReject, recurringExpenses = [],
  pendingIncomes = [],
  budgetCategories = [], isProcessing = false, token, t 
}) => {
  const [editedAmount, setEditedAmount] = useState(0);
  const [editedCategory, setEditedCategory] = useState('');
  const [transactionType, setTransactionType] = useState('expense');
  const [showRawEmail, setShowRawEmail] = useState(false);
  const [linkedExpenseId, setLinkedExpenseId] = useState('');
  const [linkedIncomeId, setLinkedIncomeId] = useState('');
  const [markAsNewRecurring, setMarkAsNewRecurring] = useState(false);
  const [showAccountSelector, setShowAccountSelector] = useState(false);
  const [pendingApprovalData, setPendingApprovalData] = useState(null);

  const expenseCategories = getCategories(budgetCategories);
  const activeCategories = transactionType === 'income' ? INCOME_CATEGORIES : expenseCategories;

  // Check if this is an informative email (no amount)
  const isInformative = email?.email_type === 'informative' || 
    (!email?.amount && !email?.parsed_data?.amount);

  useEffect(() => {
    if (email) {
      const p = email.parsed_data || {};
      setEditedAmount(email.amount || p.amount || 0);
      setEditedCategory('');
      setTransactionType(email.transaction_type || p.transaction_type || 'expense');
      setLinkedExpenseId('');
      setLinkedIncomeId('');
      setMarkAsNewRecurring(false);
    }
  }, [email]);

  if (!email) return null;

  // Render informative email modal
  if (isInformative) {
    return (
      <InformativeEmailModal 
        email={email}
        onClose={onClose}
        onMarkAsRead={() => onReject(email.id)}
        isProcessing={isProcessing}
        t={t}
      />
    );
  }

  const parsed = email.parsed_data || {};
  const merchant = email.description || parsed.merchant || t('frugal.emails.unknownMerchant');
  const cardLastFour = parsed.card_last_four;
  const transactionDate = parsed.date || email.created_at?.split('T')[0];
  const canApprove = editedCategory !== '';

  const handleApproveClick = () => {
    if (!canApprove) return;
    setPendingApprovalData({
      amount: editedAmount, 
      category: editedCategory,
      transaction_type: transactionType,
      linked_expense_id: transactionType === 'expense' ? (linkedExpenseId || null) : null,
      linked_income_id: transactionType === 'income' ? (linkedIncomeId || null) : null,
      create_new_recurring: transactionType === 'expense' && markAsNewRecurring && !linkedExpenseId
    });
    setShowAccountSelector(true);
  };

  const handleAccountSelect = (account) => {
    if (pendingApprovalData) {
      onApprove(email.id, {
        ...pendingApprovalData,
        account_id: account.id
      });
      setShowAccountSelector(false);
      setPendingApprovalData(null);
    }
  };

  const handleLinkChange = (expenseId) => {
    setLinkedExpenseId(expenseId);
    if (expenseId) {
      setMarkAsNewRecurring(false);
      const linkedExp = recurringExpenses.find(e => e.id === expenseId);
      if (linkedExp?.category) setEditedCategory(linkedExp.category);
    }
  };

  const suggestedExpenses = recurringExpenses.filter(exp => 
    exp.name?.toLowerCase().includes(merchant.toLowerCase()) ||
    merchant.toLowerCase().includes(exp.name?.toLowerCase() || '')
  );

  return (
    <>
      <div className="fixed inset-0 z-[55] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
        <div className="bg-slate-900 rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden border border-white/10">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-400" />
              <h2 className="text-lg font-semibold text-white">{t('frugal.emails.detail')}</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
            {/* Merchant & Card */}
            <div className="text-center py-2">
              <div className="text-white font-medium text-lg">{merchant}</div>
              {cardLastFour && (
                <div className="text-slate-500 text-sm flex items-center justify-center gap-1 mt-1">
                  <CreditCard className="w-4 h-4" /> **** {cardLastFour}
                </div>
              )}
            </div>

            {/* Transaction Type Toggle */}
            <div className="bg-white/5 rounded-xl p-3">
              <label className="text-xs text-slate-500 block mb-2">{t('frugal.emails.transactionType', 'Tipo de transaccion')}</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => { setTransactionType('expense'); setEditedCategory(''); }}
                  data-testid="email-type-expense"
                  className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                    transactionType === 'expense'
                      ? 'bg-rose-500 text-white' : 'bg-white/10 text-slate-400 hover:bg-white/20'
                  }`}
                >
                  {t('frugal.emails.expense', 'Gasto')}
                </button>
                <button
                  onClick={() => { setTransactionType('income'); setEditedCategory(''); }}
                  data-testid="email-type-income"
                  className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                    transactionType === 'income'
                      ? 'bg-emerald-500 text-white' : 'bg-white/10 text-slate-400 hover:bg-white/20'
                  }`}
                >
                  {t('frugal.emails.income', 'Ingreso')}
                </button>
              </div>
            </div>

            {/* Amount */}
            <div className="bg-white/5 rounded-xl p-4">
              <label className="text-xs text-slate-500 block mb-2">{t('frugal.emails.amount')}</label>
              <div className="flex items-center gap-2">
                <span className="text-emerald-400 text-2xl font-bold">$</span>
                <input type="number" step="0.01" value={editedAmount}
                  onChange={(e) => setEditedAmount(parseFloat(e.target.value) || 0)}
                  className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-2xl font-bold text-emerald-400 focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>

            {/* Link to Recurring - only for expenses */}
            {transactionType === 'expense' && (
            <div className="bg-white/5 rounded-xl p-4">
              <label className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                <Link2 className="w-4 h-4" />{t('frugal.emails.linkToRecurring')}
              </label>
              <select value={linkedExpenseId} onChange={(e) => handleLinkChange(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none">
                <option value="" className="bg-slate-800">{t('frugal.emails.noLink')}</option>
                {suggestedExpenses.length > 0 && (
                  <optgroup label={t('frugal.emails.suggested')}>
                    {suggestedExpenses.map(exp => (
                      <option key={`sug-${exp.id}`} value={exp.id} className="bg-slate-800">
                        {exp.name} - ${exp.amount?.toFixed(2)}
                      </option>
                    ))}
                  </optgroup>
                )}
                {recurringExpenses.length > 0 && (
                  <optgroup label={t('frugal.emails.allRecurring')}>
                    {recurringExpenses.map(exp => (
                      <option key={exp.id} value={exp.id} className="bg-slate-800">{exp.name} - ${exp.amount?.toFixed(2)}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
            )}

            {/* New Recurring Checkbox */}
            {transactionType === 'expense' && !linkedExpenseId && (
              <div className="bg-white/5 rounded-xl p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={markAsNewRecurring} onChange={(e) => setMarkAsNewRecurring(e.target.checked)}
                    className="w-5 h-5 rounded border-white/20 bg-white/10 text-emerald-500" />
                  <div className="flex items-center gap-2">
                    <Plus className="w-4 h-4 text-amber-400" />
                    <span className="text-white">{t('frugal.emails.markAsNewRecurring')}</span>
                  </div>
                </label>
              </div>
            )}

            {/* Link to Existing Income - only for income type */}
            {transactionType === 'income' && pendingIncomes.length > 0 && (
            <div className="bg-white/5 rounded-xl p-4">
              <label className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                <Link2 className="w-4 h-4" />{t('frugal.emails.linkToIncome', 'Vincular a ingreso existente')}
              </label>
              <select value={linkedIncomeId} onChange={(e) => setLinkedIncomeId(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                data-testid="email-link-income-select">
                <option value="" className="bg-slate-800">{t('frugal.emails.createNewIncome', '+ Crear nuevo ingreso')}</option>
                {pendingIncomes.map(inc => (
                  <option key={inc.id} value={inc.id} className="bg-slate-800">
                    {inc.name || inc.source} - ${inc.amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({t('frugal.emails.pending', 'pendiente')})
                  </option>
                ))}
              </select>
            </div>
            )}

            {/* Category */}
            <div className="bg-white/5 rounded-xl p-4">
              <label className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                <Tag className="w-4 h-4" />{t('frugal.emails.category')} <span className="text-red-400">*</span>
              </label>
              <select value={editedCategory} onChange={(e) => setEditedCategory(e.target.value)}
                className={`w-full bg-white/10 border rounded-lg px-3 py-2 text-white ${!editedCategory ? 'border-amber-500/50' : 'border-white/20'}`}>
                <option value="" className="bg-slate-800">{t('frugal.emails.selectCategory')}</option>
                {activeCategories.map(cat => (<option key={cat} value={cat} className="bg-slate-800">{translateCategory(t, cat)}</option>))}
              </select>
              {!editedCategory && (
                <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />{t('frugal.emails.categoryRequired')}
                </p>
              )}
            </div>

            {/* Date */}
            {transactionDate && (
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                <Calendar className="w-5 h-5 text-sky-400" />
                <div>
                  <div className="text-xs text-slate-500">{t('frugal.emails.date')}</div>
                  <div className="text-white">{transactionDate}</div>
                </div>
              </div>
            )}

            {/* Raw Email Toggle */}
            <button onClick={() => setShowRawEmail(!showRawEmail)}
              className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg">
              <div className="flex items-center gap-2">
                {showRawEmail ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                <span className="text-slate-300">{t('frugal.emails.viewRaw')}</span>
              </div>
            </button>
          </div>

          {/* Actions */}
          {email.status === 'pending_review' && (
            <div className="p-4 border-t border-white/10 space-y-2">
              <div className="flex gap-3">
                <button onClick={() => onReject(email.id)} disabled={isProcessing}
                  className="flex-1 py-3 bg-rose-500/20 hover:bg-rose-500/30 text-rose-400 rounded-xl font-medium disabled:opacity-50">
                  {t('frugal.emails.reject')}
                </button>
                <button onClick={handleApproveClick} disabled={isProcessing || !canApprove}
                  className={`flex-1 py-3 rounded-xl font-medium disabled:opacity-50 ${canApprove ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-slate-700 text-slate-400'}`}>
                  {isProcessing ? '...' : t('frugal.emails.approve')}
                </button>
              </div>
              <button onClick={onClose} className="w-full py-2 bg-white/5 hover:bg-white/10 text-slate-400 rounded-xl text-sm">
                {t('frugal.emails.reviewLater')}
              </button>
            </div>
          )}
        </div>
      </div>

      <RawEmailView email={email} isOpen={showRawEmail} onClose={() => setShowRawEmail(false)} t={t} />
      <AccountSelector isOpen={showAccountSelector} onClose={() => setShowAccountSelector(false)}
        onSelect={handleAccountSelect} amount={editedAmount} description={merchant} token={token} t={t} />
    </>
  );
};

export default EmailDetailModal;